import React, {useContext, useMemo, useState} from "react";
import "./ContentOutlay.scss";
import {EventUpdatedContext} from "../../../store/EventUpdatedContext";
import {getIdWithToken} from "../../../utils/storage/Token";
import FCSpinner from "../../FCObjets/FCSpinner";
import {TypeForm} from "../../../types/EnumTypeForm";
import FCContentModalFL, {DataForm} from "../../FCObjets/Form/FCContentModalFL";
import {DeleteOutlay, useFetchOutlay} from "../../../service/Business/OutlayService";
import {OutlayInterface} from "../../../service/Business/Interfaces/OutlayInterface";
import FCTag from "../../FCObjets/FCTag";
import StatusOutlay , {TypeStatusOutlay}  from "../../../utils/Business/StatusOutlay";
import FCToggleSwitch from "../../FCObjets/FCToggleSwitch";
import FCSortableHeader from "../../FCObjets/FCSortableHeader";
import {useTableSort} from "../../../hook/useTableSort";
import formatEuro from "../../../utils/formatEuro";
import {useToast} from "../../FCObjets/FCToast/FCToast";

type OutlaySortKey = "label" | "state" | "billing" | "total";

function ContentOutlay() {
    const toast = useToast();

    const {cptUpdated, setUpdated} = useContext(EventUpdatedContext);
    const {data: outlays, loading, error} = useFetchOutlay(getIdWithToken(), cptUpdated);
    const [showOutlayPayed, setShowOutlayPayed] = useState(false);
    
    const [searchQuery, setSearchQuery] = useState("");

    const [data, setData] = useState<DataForm>({
        type: TypeForm.EMPTY,
        data: undefined,
    });

    const gestionModal = (open: boolean, e: OutlayInterface | any | undefined, type: TypeForm | undefined) => {
        setData({
            type: (open && type) ? type : TypeForm.EMPTY,
            data: e
        });
    };

    const getTotal = (outlay: OutlayInterface): number => {
        if (!outlay.outlayItems) return 0;
        return outlay.outlayItems.reduce((acc, item) => acc + item.price, 0);
    };

    const filteredOutlays = useMemo(() => {
        if (!outlays) return [];
        const q = searchQuery.toLowerCase();
        return outlays.filter((o: OutlayInterface) =>
            ((o.state !== TypeStatusOutlay.PAID && o.state !== TypeStatusOutlay.CANCELED) || showOutlayPayed) &&
            (o.label.toLowerCase().includes(q) ||
            (o.billing?.number && o.billing.number.toString().includes(q)))
        );
    }, [outlays, searchQuery, showOutlayPayed]);

    const {sortedData: sortedOutlays, sortState, handleSort} = useTableSort<OutlayInterface, OutlaySortKey>(
        filteredOutlays,
        {
            label: (o) => o.label,
            state: (o) => o.state,
            billing: (o) => o.billing?.number ?? "",
            total: (o) => getTotal(o),
        },
    );

    const stats = useMemo(() => {
        if (!outlays) return {total: 0, count: 0, enAttente: 0};
        let total = 0;
        let enAttente = 0;
        outlays.forEach((o: OutlayInterface) => {
            const t = getTotal(o);
            total += t;
            if (o.state === TypeStatusOutlay.INITIALIZED || o.state === TypeStatusOutlay.SENT) enAttente += t;
        });
        return {total, count: outlays.length, enAttente};
    }, [outlays, showOutlayPayed]);

    const handleDelete = (outlay: OutlayInterface) => {
        const payload = {
            title: `Suppression de la note de frais ${outlay.label}`,
            msg: "Êtes-vous sûr de vouloir supprimer cette note de frais ?",
            onConfirm: () => DeleteOutlay(outlay).then(() => {
                toast.success("Frais de déplacement supprimés.", "Frais de déplacement");
                setUpdated();
            })
        };
        gestionModal(true, payload, TypeForm.CONFIRM);
    };

    const renderActions = (outlay: OutlayInterface, isMobile: boolean) => {
        const btnClass = isMobile ? "outlayMobileActionBtn" : "outlayActionBtn";
        return (
            <>
                <button className={`${btnClass} outlayActionEdit`}
                        onClick={() => gestionModal(true, outlay, TypeForm.OUTLAY)}
                        title="Modifier">
                    <span className="material-symbols-outlined">edit</span>
                    {isMobile && "Modifier"}
                </button>
                <button className={`${btnClass} outlayActionPdf`}
                        onClick={() => {
                            window.open(`/pdf/outlay/${outlay.id}`, '_blank');
                            setTimeout(() => setUpdated(), 3000);
                        }}
                        title="Générer débours">
                    <span className="material-symbols-outlined">picture_as_pdf</span>
                    {isMobile && "PDF"}
                </button>
                <button className={`${btnClass} outlayActionDelete`}
                        onClick={() => handleDelete(outlay)}
                        title="Supprimer">
                    <span className="material-symbols-outlined">delete</span>
                    {isMobile && "Supprimer"}
                </button>
            </>
        );
    };

    return (
        <FCContentModalFL form={data} onModalFormFinished={() => {
            gestionModal(false, undefined, undefined);
            setUpdated();
        }}>
            <div className="outlayPage">
                {/* Header */}
                <div className="outlayHeader">
                    <div>
                        <h1 className="outlayTitle">Frais de déplacement</h1>
                        <p className="outlaySubtitle">Gérez vos notes de frais et débours professionnels</p>
                    </div>
                    <div className="outlayHeaderRight">
                        <div className="outlaySearchBar">
                            <span className="material-symbols-outlined outlaySearchIcon">search</span>
                            <input className="outlaySearchInput"
                                    type="text"
                                    placeholder="Rechercher une note de frais..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}/>
                        </div>
                        <button className="outlayBtnAdd"
                                onClick={() => gestionModal(true, undefined, TypeForm.OUTLAY)}>
                            <span className="material-symbols-outlined">add</span>
                            Nouvelle note
                        </button>
                    </div>
                </div>

                {loading && <div className="outlayLoading"><FCSpinner size="middle"/></div>}

                {error && <div className="outlayEmptyState">{error.message}</div>}

                {outlays && (
                    <>
                        {/* KPI Cards */}
                        <div className="outlayKpiGrid">
                            <div className="outlayKpiCard outlayKpiTotal">
                                <div className="outlayKpiLabel">Total des frais</div>
                                <div className="outlayKpiValueLg">{formatEuro(stats.total)}</div>
                                <div className="outlayKpiSub">{stats.count} note{stats.count > 1 ? "s" : ""} de frais</div>
                            </div>
                            <div className="outlayKpiCard">
                                <div className="outlayKpiLabel">En attente</div>
                                <div className="outlayKpiValue">{formatEuro(stats.enAttente)}</div>
                                <div className="outlayKpiSub">À valider / envoyé</div>
                            </div>
                            <div className="outlayKpiCard">
                                <div className="outlayKpiLabel">Notes de frais</div>
                                <div className="outlayKpiValue">{stats.count}</div>
                                <div className="outlayKpiSub">Total enregistrées</div>
                            </div>
                        </div>

                        {/* Search + toggle */}
                        <div className="outlaySectionHeader">
                            <div className="outlaySectionActions">
                                <FCToggleSwitch
                                    onToggle={(isChecked) => setShowOutlayPayed(isChecked)}
                                    defaultChecked={false}
                                    label="Payées / Annulées"
                                />
                            </div>
                        </div>

                        {/* Desktop Table */}
                        <div className="outlayTableWrapper">
                            <table className="outlayTable">
                                <thead>
                                <tr>
                                    <th><FCSortableHeader label="Description" sortKey="label" sortState={sortState} onSort={handleSort}/></th>
                                    <th><FCSortableHeader label="Statut" sortKey="state" sortState={sortState} onSort={handleSort}/></th>
                                    <th><FCSortableHeader label="Facture associée" sortKey="billing" sortState={sortState} onSort={handleSort}/></th>
                                    <th><FCSortableHeader label="Total" sortKey="total" sortState={sortState} onSort={handleSort}/></th>
                                    <th>Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {sortedOutlays.map((outlay: OutlayInterface, index: React.Key) => {
                                    const total = getTotal(outlay);
                                    return (
                                        <tr key={index}>
                                            <td><span className="outlayCellLabel">{outlay.label}</span></td>
                                            <td>
                                                <FCTag value={StatusOutlay.toString(outlay.state)}
                                                       color={StatusOutlay.toColor(outlay.state)}
                                                       style={{fontWeight: "bold"}}/>
                                            </td>
                                            <td>
                                                {outlay.billing ? (
                                                    <span className="outlayCellBilling">{outlay.billing.number}</span>
                                                ) : (
                                                    <span style={{color: "var(--text-light)"}}>—</span>
                                                )}
                                            </td>
                                            <td><span className="outlayCellAmount">{formatEuro(total)}</span></td>
                                            <td>
                                                <div className="outlayCellActions">
                                                    {renderActions(outlay, false)}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {sortedOutlays.length === 0 && (
                                    <tr>
                                        <td colSpan={5}>
                                            <div className="outlayEmptyState">Aucune note de frais trouvée</div>
                                        </td>
                                    </tr>
                                )}
                                </tbody>
                            </table>
                            <div className="outlayTableFooter">
                                <span className="outlayTableCount">
                                    {sortedOutlays.length} note{sortedOutlays.length > 1 ? "s" : ""} de frais
                                </span>
                            </div>
                        </div>

                        {/* Mobile Cards */}
                        <div className="outlayMobileList">
                            {sortedOutlays.map((outlay: OutlayInterface, index: React.Key) => {
                                const total = getTotal(outlay);
                                return (
                                    <div className="outlayMobileCard" key={index}>
                                        <div className="outlayMobileCardHeader">
                                            <div>
                                                <h3 className="outlayMobileCardName">{outlay.label}</h3>
                                                <FCTag value={StatusOutlay.toString(outlay.state)}
                                                       color={StatusOutlay.toColor(outlay.state)}
                                                       style={{fontWeight: "bold"}}/>
                                            </div>
                                        </div>
                                        <div className="outlayMobileCardMeta">
                                            {outlay.billing ? `Facture ${outlay.billing.number}` : "Aucune facture associée"}
                                        </div>
                                        <div className="outlayMobileCardAmounts">
                                            <div className="outlayMobileCardAmountCol">
                                                <span className="outlayMobileCardAmountLabel">Total</span>
                                                <span className="outlayMobileCardAmountValue">{formatEuro(total)}</span>
                                            </div>
                                        </div>
                                        <div className="outlayMobileCardActions">
                                            {renderActions(outlay, true)}
                                        </div>
                                    </div>
                                );
                            })}
                            {sortedOutlays.length === 0 && (
                                <div className="outlayEmptyState">Aucune note de frais trouvée</div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </FCContentModalFL>
    );
}

export default ContentOutlay;
